import { useEffect, useState } from 'react';
import Page from '../../../../components/Page';
import adminServices from '../../../../services/adminServices';
import ResponsiveTable from '../../../../components/Table/Responsivetable';

// ─── Progress Bar ────────────────────────────────────────────────────────────
function ProgressBar({ value }) {
  const pct = Math.min(100, Math.max(0, parseFloat(value) || 0));

  // Fix: no-nested-ternary — use a function instead
  function getColor(p) {
    if (p === 100) return 'bg-green-500';
    if (p >= 50)   return 'bg-blue-500';
    if (p > 0)     return 'bg-orange-400';
    return 'bg-gray-300';
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${getColor(pct)}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-10 text-right">{pct}%</span>
    </div>
  );
}

function CourseModal({ client, open, onClose }) {
  const [search, setSearch]           = useState('');
  const [sortBy, setSortBy]           = useState('completionRate');
  const [sortDir, setSortDir]         = useState('desc');
  const [downloading, setDownloading] = useState(false);

  if (!open || !client) return null;

  const courses = client.courses || [];

  const filtered = courses
    .filter((c) =>
      c.courseTitle?.toLowerCase().includes(search.toLowerCase()) ||
      c.format?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-500 ml-1">{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  const formatBadge = (type) => {
    const map = {
      simulation:  { label: 'Simulation',  cls: 'bg-purple-100 text-purple-700' },
      video:       { label: 'Video',        cls: 'bg-blue-100 text-blue-700' },
      combination: { label: 'Combination',  cls: 'bg-teal-100 text-teal-700' },
    };
    const m = map[type] || { label: type, cls: 'bg-gray-100 text-gray-600' };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>
    );
  };

  const totalAssigned   = courses.reduce((s, c) => s + (c.totalAssigned   || 0), 0);
  const totalCompleted  = courses.reduce((s, c) => s + (c.completedCount  || 0), 0);
  const totalInProgress = courses.reduce((s, c) => s + (c.inProgressCount || 0), 0);
  const totalNotStarted = courses.reduce((s, c) => s + (c.notStartedCount || 0), 0);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { default: generateClientReport } = await import('./Generateclientreport');
      generateClientReport(client);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Fix: sortable column labels — replace bare <span onClick> with <button>
  // to satisfy jsx-a11y/click-events-have-key-events &
  //             jsx-a11y/no-static-element-interactions
  const sortButton = (label, col) => (
    <button
      type="button"
      onClick={() => handleSort(col)}
      className="flex items-center gap-0.5 cursor-pointer select-none bg-transparent border-0 p-0 font-medium text-xs text-gray-500 uppercase tracking-wide"
    >
      {label} <SortIcon col={col} />
    </button>
  );

  const courseColumns = [
    {
      key: 'courseTitle',
      label: 'Course',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900 leading-snug">{row.courseTitle}</p>
          <div className="mt-1">{formatBadge(row.format)}</div>
        </div>
      ),
    },
    {
      key: 'totalAssigned',
      label: sortButton('Assigned', 'totalAssigned'),
      render: (row) => <span className="font-medium text-gray-700 block text-center">{row.totalAssigned}</span>,
    },
    {
      key: 'completedCount',
      label: sortButton('Completed', 'completedCount'),
      render: (row) => <span className="font-medium text-green-600 block text-center">{row.completedCount}</span>,
    },
    {
      key: 'inProgressCount',
      label: sortButton('In Progress', 'inProgressCount'),
      render: (row) => <span className="font-medium text-orange-500 block text-center">{row.inProgressCount}</span>,
    },
    {
      key: 'notStartedCount',
      label: sortButton('Not Started', 'notStartedCount'),
      render: (row) => <span className="font-medium text-gray-400 block text-center">{row.notStartedCount}</span>,
    },
    {
      key: 'completionRate',
      label: sortButton('Completion Rate', 'completionRate'),
      render: (row) => <ProgressBar value={row.completionRate} />,
    },
  ];

  // Fix: modal backdrop — replace bare <div onClick> with role+keyboard support
  const handleBackdropKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      {/* Backdrop — keyboard + role fixes */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        className="absolute inset-0"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onKeyDown={handleBackdropKeyDown}
      />

      <div
        className="relative bg-white rounded-xl shadow-xl flex flex-col"
        style={{ width: '1100px', maxWidth: '96vw', maxHeight: '90vh' }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{client.organizationName}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                client.subscriptionStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {client.subscriptionStatus || 'N/A'}
              </span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                {client.type || 'B2B'}
              </span>
              {client.websiteUrl && (
                <span className="text-xs text-gray-400">{client.websiteUrl}</span>
              )}
              {client.contractStartDate && (
                <span className="text-xs text-gray-500">
                  {new Date(client.contractStartDate).toLocaleDateString()} —{' '}
                  {new Date(client.contractEndDate).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {/* Right side: Download + Close */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {downloading ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              {downloading ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total Assigned', value: totalAssigned,   bg: 'bg-blue-50',   text: 'text-blue-700',   num: 'text-blue-900' },
              { label: 'Completed',      value: totalCompleted,  bg: 'bg-green-50',  text: 'text-green-700',  num: 'text-green-900' },
              { label: 'In Progress',    value: totalInProgress, bg: 'bg-orange-50', text: 'text-orange-700', num: 'text-orange-900' },
              { label: 'Not Started',    value: totalNotStarted, bg: 'bg-gray-50',   text: 'text-gray-600',   num: 'text-gray-900' },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-lg p-4`}>
                <p className={`text-xs font-medium uppercase tracking-wide ${s.text}`}>{s.label}</p>
                <p className={`text-2xl font-bold mt-1 ${s.num}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Course table */}
          <ResponsiveTable columns={courseColumns} data={filtered} loading={false} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientReports() {
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(0);
  const PAGE_SIZE                       = 20;

  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter]     = useState('ALL');
  const [searchInput, setSearchInput]   = useState('');

  const [sortBy, setSortBy]             = useState('organizationName');
  const [sortDir, setSortDir]           = useState('asc');
  const [selected, setSelected]         = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);

  const fetchData = (p = 0, s = search, st = statusFilter, ty = typeFilter) => {
    setLoading(true);
    adminServices
      .getClientReports({ page: p, size: PAGE_SIZE, search: s, status: st, type: ty })
      .then((r) => {
        if (r.ok) {
          setClients(Array.isArray(r.data?.data) ? r.data.data : []);
          setTotal(r.data?.total || r.total || 0);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(0); }, []);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
    fetchData(0, searchInput, statusFilter, typeFilter);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(0);
    fetchData(0, search, val, typeFilter);
  };

  const handleTypeChange = (val) => {
    setTypeFilter(val);
    setPage(0);
    fetchData(0, search, statusFilter, val);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-500 ml-1">{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  // Fix: sortable header labels — use <button> instead of bare <span onClick>
  const sortButton = (label, col) => (
    <button
      type="button"
      onClick={() => handleSort(col)}
      className="flex items-center gap-0.5 cursor-pointer select-none bg-transparent border-0 p-0 font-medium text-xs text-gray-500 uppercase tracking-wide"
    >
      {label} <SortIcon col={col} />
    </button>
  );

  const sorted = [...clients].sort((a, b) => {
    let av = a[sortBy] ?? '';
    let bv = b[sortBy] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Fix: no-nested-ternary in status badge — extract to a function
  function getStatusClass(status) {
    if (status === 'ACTIVE')     return 'bg-green-100 text-green-700';
    if (status === 'RESTRICTED') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  }

  const columns = [
    {
      key: 'index',
      label: '#',
      render: (row, idx) => (
        <span className="text-gray-400">{page * PAGE_SIZE + idx + 1}</span>
      ),
    },
    {
      key: 'organizationName',
      label: sortButton('Organization', 'organizationName'),
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.organizationName}</p>
          {row.websiteUrl && <p className="text-xs text-gray-400">{row.websiteUrl}</p>}
        </div>
      ),
    },
    {
      key: 'type',
      label: sortButton('Type', 'type'),
      render: (row) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          row.type === 'B2B' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
        }`}>
          {row.type || 'B2B'}
        </span>
      ),
    },
    {
      key: 'subscriptionStatus',
      label: sortButton('Status', 'subscriptionStatus'),
      render: (row) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(row.subscriptionStatus)}`}>
          {row.subscriptionStatus || 'N/A'}
        </span>
      ),
    },
    {
      key: 'learnerSeats',
      label: sortButton('Learner Seats', 'learnerSeats'),
      render: (row) => <span className="text-gray-700">{row.learnerSeats ?? '-'}</span>,
    },
    {
      key: 'contractEndDate',
      label: sortButton('Contract End', 'contractEndDate'),
      render: (row) => (
        <span className="text-gray-500 text-xs">
          {row.contractEndDate ? new Date(row.contractEndDate).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      key: 'totalCourses',
      label: 'Total Courses',
      render: (row) => {
        const courses = row.courses || [];
        return <span className="font-medium text-gray-700 text-center block">{courses.length}</span>;
      },
    },
    {
      key: 'totalAssigned',
      label: 'Total Assigned',
      render: (row) => {
        const courses = row.courses || [];
        const rowTotal = courses.reduce((s, c) => s + (c.totalAssigned || 0), 0);
        return <span className="font-medium text-gray-700 text-center block">{rowTotal}</span>;
      },
    },
    {
      key: 'avgCompletion',
      label: 'Avg Completion',
      render: (row) => {
        const courses = row.courses || [];
        const avg = courses.length > 0
          ? (courses.reduce((s, c) => s + (parseFloat(c.completionRate) || 0), 0) / courses.length).toFixed(1)
          : '0.0';
        return <ProgressBar value={avg} />;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          onClick={() => { setSelected(row); setModalOpen(true); }}
          title="View Course Report"
          className="text-blue-500 hover:text-blue-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <Page title="Client Reports">
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Client Reports</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
            />
            <button
              type="button"
              onClick={handleSearch}
              className="border border-gray-200 rounded-lg px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700"
            >
              Search
            </button>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="RESTRICTED">Restricted</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Types</option>
            <option value="B2B">B2B</option>
            <option value="B2C">B2C</option>
          </select>

          <span className="ml-auto text-sm text-gray-500 self-center">{total} clients</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <ResponsiveTable columns={columns} data={sorted} loading={loading} />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i)
                .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 2)
                .reduce((acc, i, idx, arr) => {
                  if (idx > 0 && i - arr[idx - 1] > 1) acc.push('...');
                  acc.push(i);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="px-3 py-1.5 text-sm text-gray-400">...</span>
                  ) : (
                    <button
                      type="button"
                      key={item}
                      onClick={() => handlePageChange(item)}
                      className={`px-3 py-1.5 text-sm border rounded-lg ${
                        page === item ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {item + 1}
                    </button>
                  )
                )
              }
              <button
                type="button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <CourseModal
        client={selected}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelected(null); }}
      />
    </Page>
  );
}